import { Icon, IconProps } from '@silverfort/sf-components';
import { FC, useRef } from 'react';
import { useGetMaxValuesToDisplay } from './useGetMaxValuesToDisplay';

export interface Props {
    icon: IconProps['icon'];
    label?: string;
    values: string[];
    shouldFitParent?: boolean;
    onResizing?: () => void;
    onFinishSizing?: () => void;
}

const ICON_WIDTH_PIXELS = 16;
const SPACING_PX_UNIT = 4;
const ICON_TO_LABEL_SPACING = 1; // NOTICE: After change this value make sure to change the corresponding value in the classname (currently `gap`)
const CONTAINER_START_SPACING = 1; // NOTICE: After change this value make sure to change the corresponding value in the classname (currently `ps`)
const CONTAINER_END_SPACING = 2; // NOTICE: After change this value make sure to change the corresponding value in the classname (currently `pe`)
const EXTRAS_WIDTH_PX = 22;

const ChipGroup: FC<Props> = ({ icon, values, label, shouldFitParent = false, onResizing, onFinishSizing }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const valuesRef = useRef<(HTMLSpanElement | null)[]>([]);

    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    const labelWidth = labelRef.current?.offsetWidth ?? 0;

    const { maxValuesToDisplay } = useGetMaxValuesToDisplay({
        shouldFitParent,
        label,
        values,
        containerWidth,
        labelWidth,
        onResizing,
        onFinishSizing,
        valuesElements: valuesRef.current,
        staticMeasures: {
            iconWidthPixels: ICON_WIDTH_PIXELS,
            spacingUnitPixels: SPACING_PX_UNIT,
            spaceBetweenIconAndLabel: ICON_TO_LABEL_SPACING,
            containerSpacingAtStart: CONTAINER_START_SPACING,
            containerSpacingAtEnd: CONTAINER_END_SPACING,
            extrasWidth: EXTRAS_WIDTH_PX
        }
    });

    const getValuesToDisplay = () => {
        const extraCount = values.length - maxValuesToDisplay;

        if (!shouldFitParent || !extraCount) {
            return values;
        }

        return [...values.slice(0, maxValuesToDisplay), `+${extraCount}`];
    };

    return (
        <div
            ref={containerRef}
            // NOTICE: After change the gaps and paddings - make sure to update the corresponding constant values above (currently ICON_TO_LABEL_SPACING for `gap`, CONTAINER_START_SPACING for `ps` and CONTAINER_END_SPACING for `pe`)
            className="flex h-6 w-fit max-w-full items-center rounded-[4px] border border-sf-gray-100 gap-1 ps-1 pe-2">
            <Icon icon={icon} size={ICON_WIDTH_PIXELS} className="fill-sf-gray-400" />
            <div className="flex">
                {!!label && (
                    <span
                        ref={labelRef}
                        className="font-sf text-12 h-6 flex items-center justify-center leading-[14.32px] whitespace-nowrap">
                        {label}:
                    </span>
                )}
                {getValuesToDisplay().map((value, index) => (
                    <span
                        key={value}
                        ref={(ref) => {
                            valuesRef.current[index] = ref;
                        }}
                        className={`font-sf text-12 h-6 whitespace-nowrap flex items-center justify-center leading-[14.32px] px-2 border-e first:ps-0 last:border-e-0 last:pe-0 border-sf-dark-100 ${
                            !!label ? 'font-medium' : 'font-normal'
                        }`}>
                        {value}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default ChipGroup;
